<template>
  <div class="p-4 w-48.75 self-stretch flex flex-col rounded-3xl bg-key">
    <div class="flex shrink-0 items-center gap-1 h-5.5">
      <Icon
        icon="Technology-Wireframe-KeyBottomDZ"
        class="size-5"
        :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
      />
      <span
        class="text-m-medium text-black-white transition-colors"
        :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
        >{{ t('itinerary.deadZone.title') }}</span
      >
    </div>
    <div class="min-h-10 shrink-0">
      <p
        class="text-s-medium transition-colors"
        :class="`${disabled ? 'text-text-disabled' : 'text-text-glass-default'}`"
      >
        {{ t('itinerary.deadZone.description') }}
      </p>
    </div>
    <div class="mt-2 flex-1 flex flex-col gap-6">
      <div>
        <h3
          class="text-m-medium transition-colors"
          :class="disabled ? 'text-text-disabled' : 'text-black-white'"
        >
          {{ t('itinerary.deadZone.top') }}
        </h3>
        <div class="mt-1.5 flex items-center gap-2 text-s-medium">
          <span :class="disabled ? 'text-text-disabled' : 'text-black-white'"> 0.0 </span>
          <div class="relative flex-1 min-w-0" :class="{ 'opacity-25': disabled }">
            <Slider
              v-model="topDisplay"
              :min="0"
              :max="0.5"
              :step="0.01"
              :disabled="disabled"
              class="w-full"
              @value-commit="commitTopDeadZone"
            />
            <div
              class="absolute top-3 left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
              :style="{ left: topBubbleLeft }"
            >
              <div class="text-black-white text-s-medium">{{ topDisplay[0].toFixed(2) }}mm</div>
            </div>
          </div>
          <span :class="disabled ? 'text-text-disabled' : 'text-black-white'"> 0.5 </span>
        </div>
      </div>
      <div>
        <h3
          class="text-m-medium transition-colors"
          :class="disabled ? 'text-text-disabled' : 'text-black-white'"
        >
          {{ t('itinerary.deadZone.bottom') }}
        </h3>
        <div class="mt-1.5 flex items-center gap-2 text-s-medium">
          <span :class="disabled ? 'text-text-disabled' : 'text-black-white'"> 0.0 </span>
          <div class="relative flex-1 min-w-0" :class="{ 'opacity-25': disabled }">
            <Slider
              v-model="bottomDisplay"
              :min="0"
              :max="0.5"
              :step="0.01"
              :disabled="disabled"
              class="w-full"
              @value-commit="commitBottomDeadZone"
            />
            <div
              class="absolute top-3 left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
              :style="{ left: bottomBubbleLeft }"
            >
              <div class="text-black-white text-s-medium">{{ bottomDisplay[0].toFixed(2) }}mm</div>
            </div>
          </div>
          <span :class="disabled ? 'text-text-disabled' : 'text-black-white'"> 0.5 </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Icon, Slider } from '@rapoo/element'
  import { useI18n } from 'vue-i18n'

  const props = defineProps<{
    topDeadZone: number
    bottomDeadZone: number
    deadZoneEnabled: boolean
  }>()

  const emit = defineEmits<{
    'update:topDeadZone': [value: number]
    'update:bottomDeadZone': [value: number]
  }>()

  const { t } = useI18n()

  const topDisplay = ref([props.topDeadZone / 100])
  watch(
    () => props.topDeadZone,
    (val) => {
      topDisplay.value = [val / 100]
    },
  )

  const bottomDisplay = ref([props.bottomDeadZone / 100])
  watch(
    () => props.bottomDeadZone,
    (val) => {
      bottomDisplay.value = [val / 100]
    },
  )

  const commitTopDeadZone = (val: number[]) => {
    if (!props.deadZoneEnabled) return
    emit('update:topDeadZone', Math.round(val[0] * 100))
  }

  const commitBottomDeadZone = (val: number[]) => {
    if (!props.deadZoneEnabled) return
    emit('update:bottomDeadZone', Math.round(val[0] * 100))
  }

  const disabled = computed(() => !props.deadZoneEnabled)

  const getBubbleLeft = (value: number) => {
    const progress = Math.min(1, Math.max(0, value / 0.5))
    const thumbWidth = 12
    return `calc(${progress * 100}% - ${progress * thumbWidth}px + ${thumbWidth / 2}px)`
  }

  const topBubbleLeft = computed(() => getBubbleLeft(topDisplay.value[0]))
  const bottomBubbleLeft = computed(() => getBubbleLeft(bottomDisplay.value[0]))
</script>

<style scoped></style>
